import React from 'react';
import { Box, Chip } from '@mui/material';

export const FONT = 'Mulish, sans-serif';

// label / background / text colour for each order status
export const ORDER_STATUS = {
  pending: { label: 'Pending', bg: '#fef3c7', fg: '#92400e' },
  placed: { label: 'New', bg: '#e0e7ff', fg: '#3730a3' },
  picking: { label: 'Picking', bg: '#e0f2fe', fg: '#075985' },
  packed: { label: 'Packed', bg: '#cffafe', fg: '#155e75' },
  'ready-for-dispatch': { label: 'Ready to Dispatch', bg: '#ccfbf1', fg: '#115e59' },
  dispatched: { label: 'Dispatched', bg: '#ede9fe', fg: '#5b21b6' },
  'in-transit': { label: 'In Transit', bg: '#f3e8ff', fg: '#6b21a8' },
  'out-for-delivery': { label: 'Out for Delivery', bg: '#fce7f3', fg: '#9d174d' },
  delivered: { label: 'Delivered', bg: '#dcfce7', fg: '#166534' },
  cancelled: { label: 'Cancelled', bg: '#fee2e2', fg: '#991b1b' },
  returned: { label: 'Returned', bg: '#ffedd5', fg: '#9a3412' },
  failed: { label: 'Failed', bg: '#f1f5f9', fg: '#475569' },
};
export const statusOf = (s) => ORDER_STATUS[s] || { label: s || '-', bg: '#f1f5f9', fg: '#475569' };

export const PAYMENT_STATUS = {
  paid: { label: 'Paid', bg: '#dcfce7', fg: '#166534' },
  pending: { label: 'Pending', bg: '#fef3c7', fg: '#92400e' },
  processing: { label: 'Processing', bg: '#e0f2fe', fg: '#075985' },
  failed: { label: 'Failed', bg: '#fee2e2', fg: '#991b1b' },
};
export const PAYMENT_MODE = { wallet: 'Wallet', upi: 'UPI', reward: 'Reward Points', card: 'Card', netbanking: 'Net Banking', cod: 'COD', cash: 'Cash', others: 'Others' };

export const pill = (bg, fg, extra = {}) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 12, ...extra });

export const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');
export const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '');
export const fmtDateTime = (d) => (d ? `${fmtDate(d)}, ${fmtTime(d)}` : '-');
export const timeAgo = (d) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  const days = Math.floor(s / 86400);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const itemName = (item) => item?.product_name || item?.product_id?.title || 'Product';
export const moneyNum = (v) => parseFloat(v) || 0;

// Payment details are a list (one entry per payment, e.g. wallet + UPI). Summarise them.
export const paymentInfo = (order) => {
  const lines = Array.isArray(order?.paymentDetails) ? order.paymentDetails : order?.paymentDetails ? [order.paymentDetails] : [];
  const modes = [...new Set(lines.map((l) => l.mode).filter(Boolean))];
  const isCod = modes.includes('cod') || (!lines.length && moneyNum(order?.codCharge) > 0);
  const method = isCod ? 'COD' : modes.length ? modes.map((m) => PAYMENT_MODE[m] || m).join(' + ') : '—';
  let status = order?.paymentStatus || 'pending';
  const s = PAYMENT_STATUS[status] || { label: status, bg: '#f1f5f9', fg: '#475569' };
  const label = isCod && status !== 'paid' ? 'To collect' : s.label;
  const style = isCod && status !== 'paid' ? { bg: '#ffedd5', fg: '#9a3412' } : s;
  return { lines, method, status, label, bg: style.bg, fg: style.fg, isCod };
};

// Expected delivery: courier ETD saved at checkout, else order date + estimated days
export const expectedDelivery = (order) => {
  if (order?.eta) {
    const d = new Date(order.eta);
    if (!isNaN(d.getTime())) return d;
  }
  if (order?.etaDays) {
    const d = new Date(order.createdAt);
    d.setDate(d.getDate() + Number(order.etaDays));
    return d;
  }
  return null;
};
export const deliveredAt = (order) => {
  const h = [...(order?.statusHistory || [])].reverse().find((s) => s.status === 'delivered');
  return h?.changedAt || null;
};

export const OPEN_STATUSES = ['pending', 'placed', 'picking', 'packed', 'ready-for-dispatch', 'dispatched', 'in-transit', 'out-for-delivery'];
export const isOpen = (order) => OPEN_STATUSES.includes(order?.orderStatus);
export const isDelayed = (order) => {
  const exp = expectedDelivery(order);
  return isOpen(order) && !!exp && exp.getTime() + 86399000 < Date.now();
};

// Happy-path progress: 1 Placed, 2 Packed, 3 Shipped, 4 Out for delivery, 5 Delivered
export const PROGRESS_STEPS = [
  { key: 'placed', label: 'Order Placed' },
  { key: 'packed', label: 'Packed' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'out', label: 'Out for Delivery' },
  { key: 'delivered', label: 'Delivered' },
];
export const progressStep = (status) => {
  switch (status) {
    case 'delivered': return 5;
    case 'out-for-delivery': return 4;
    case 'dispatched':
    case 'in-transit': return 3;
    case 'packed':
    case 'ready-for-dispatch': return 2;
    case 'picking':
    case 'placed':
    case 'pending': return 1;
    default: return 0;
  }
};

export const StatusChip = ({ status, size = 'small' }) => {
  const s = statusOf(status);
  return <Chip size={size} label={s.label} sx={pill(s.bg, s.fg)} />;
};

// Five small segments showing how far an order has got.
export const MiniProgress = ({ status }) => {
  const done = progressStep(status);
  if (!done) return null;
  const color = statusOf(status).fg;
  return (
    <Box sx={{ display: 'flex', gap: '3px', mt: 0.75, width: 96 }}>
      {PROGRESS_STEPS.map((s, i) => (
        <Box key={s.key} sx={{ flex: 1, height: 4, borderRadius: 2, background: i < done ? color : '#e2e8f0' }} />
      ))}
    </Box>
  );
};
